import { getApiBaseUrl } from '../../../utils/apiConfig';

const API_BASE_URL = getApiBaseUrl();

function authHeaders() {
  const token = localStorage.getItem('authToken');
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

async function parseJson(res) {
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) {
    throw new Error(data.message || `Erro HTTP ${res.status}`);
  }
  return data;
}

/** Situação da instalação do App Go do técnico: { status: 'ok'|'attention'|'unknown'|'none', issues[] }. */
export async function fetchTechnicianGoInstallStatus(technicianId) {
  const res = await fetch(
    `${API_BASE_URL}/api/technician-panel/${encodeURIComponent(technicianId)}/go-install`,
    { headers: authHeaders() }
  );
  const data = await parseJson(res);
  return data?.data || null;
}

export async function fetchRouteSchedule(technicianId, dateYmd) {
  const qs = dateYmd ? `?date=${encodeURIComponent(dateYmd)}` : '';
  const res = await fetch(`${API_BASE_URL}/api/orders/route/${encodeURIComponent(technicianId)}${qs}`, {
    headers: authHeaders(),
  });
  const data = await parseJson(res);
  return Array.isArray(data.data) ? data.data : Array.isArray(data) ? data : [];
}

export async function fetchTodayRoute(technicianId) {
  const res = await fetch(`${API_BASE_URL}/api/orders/today-route/${encodeURIComponent(technicianId)}`, {
    headers: authHeaders(),
  });
  const data = await parseJson(res);
  return Array.isArray(data.data) ? data.data : Array.isArray(data) ? data : [];
}

export async function fetchCompletedRoute(technicianId) {
  const res = await fetch(`${API_BASE_URL}/api/orders/completed-route/${encodeURIComponent(technicianId)}`, {
    headers: authHeaders(),
  });
  const data = await parseJson(res);
  return Array.isArray(data.data) ? data.data : Array.isArray(data) ? data : [];
}

export async function fetchMonitoramentoTechnician(technicianId, dateYmd) {
  const qs = dateYmd ? `?data=${encodeURIComponent(dateYmd)}` : '';
  const res = await fetch(
    `${API_BASE_URL}/api/monitoramento/externo/technician/${encodeURIComponent(technicianId)}${qs}`,
    { headers: authHeaders() }
  );
  const data = await parseJson(res);
  return data.data || null;
}

export async function fetchTechnicianPanelMapPlan(technicianId, day = 'amanha') {
  const qs = `?day=${encodeURIComponent(day === 'hoje' ? 'hoje' : 'amanha')}`;
  const res = await fetch(
    `${API_BASE_URL}/api/technician-panel/${encodeURIComponent(technicianId)}/map-plan${qs}`,
    { headers: authHeaders() }
  );
  const data = await parseJson(res);
  return data.data || null;
}

/** Empresas + endereço do técnico para “ponto de partida” no Roteirizar. */
export async function fetchTechnicianPanelMapStartPoints(technicianId) {
  const res = await fetch(
    `${API_BASE_URL}/api/technician-panel/${encodeURIComponent(technicianId)}/map-start-points`,
    { headers: authHeaders() }
  );
  const data = await parseJson(res);
  return Array.isArray(data?.data?.items) ? data.data.items : [];
}

/**
 * Posição GPS ao vivo (MySQL go_user_live_location).
 * Em deslocamento com trip_id, o backend prioriza o último ponto de go_location_track.
 */
export async function fetchTechnicianPanelLiveLocation(technicianId) {
  const res = await fetch(
    `${API_BASE_URL}/api/technician-panel/${encodeURIComponent(technicianId)}/live-location`,
    { headers: authHeaders() }
  );
  const data = await parseJson(res);
  return data.data || null;
}

/** Últimos eventos (LCMY_GO_EVENTS) para o LiveMap. */
export async function fetchTechnicianPanelGoEvents(technicianId) {
  const res = await fetch(
    `${API_BASE_URL}/api/technician-panel/${encodeURIComponent(technicianId)}/events`,
    { headers: authHeaders() }
  );
  const data = await parseJson(res);
  return Array.isArray(data?.data?.events) ? data.data.events : [];
}

/** Rastreio GPS do dia (go_location_track). dateYmd = dia em Brasília. */
export async function fetchTechnicianPanelLocationTrack(technicianId, dateYmd) {
  const qs = dateYmd ? `?date=${encodeURIComponent(dateYmd)}` : '';
  const res = await fetch(
    `${API_BASE_URL}/api/technician-panel/${encodeURIComponent(technicianId)}/location-track${qs}`,
    { headers: authHeaders() }
  );
  const data = await parseJson(res);
  return data.data || { date: dateYmd || null, points: [] };
}

/** Dias BRT com GPS no intervalo [from, to) — payload leve (só lista de YMD). */
export async function fetchTechnicianPanelTrackBusyDays(technicianId, fromYmd, toYmd, opts = {}) {
  const qs = `?from=${encodeURIComponent(fromYmd)}&to=${encodeURIComponent(toYmd)}`;
  const res = await fetch(
    `${API_BASE_URL}/api/technician-panel/${encodeURIComponent(technicianId)}/location-track/busy-days${qs}`,
    { headers: authHeaders(), signal: opts.signal }
  );
  const data = await parseJson(res);
  return Array.isArray(data?.data?.days) ? data.data.days : [];
}

/** Diário técnico (Orya + go_app_log). */
export async function postDiarioOryaChat(
  technicianId,
  { date, prompt, history, timeFrom, timeTo, technicianName, chatUserName },
  opts = {}
) {
  const res = await fetch(
    `${API_BASE_URL}/api/technician-panel/${encodeURIComponent(technicianId)}/diario-orya`,
    {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({
        date,
        prompt,
        history: Array.isArray(history) ? history : [],
        timeFrom: timeFrom || undefined,
        timeTo: timeTo || undefined,
        technicianName: technicianName || undefined,
        chatUserName: chatUserName || undefined,
      }),
      signal: opts.signal,
    }
  );
  const data = await parseJson(res);
  return data.data || {};
}

/** Batch leve: IDs com GPS fresco (< 30 min). Sem lat/lng. */
export async function fetchTechnicianPanelTrackableBatch(technicianIds) {
  const ids = Array.isArray(technicianIds)
    ? [...new Set(technicianIds.map((id) => String(id ?? '').trim()).filter(Boolean))]
    : [];
  if (!ids.length) return { trackableIds: [], maxAgeMinutes: 30 };
  const res = await fetch(`${API_BASE_URL}/api/technician-panel/live-location/trackable`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ technicianIds: ids }),
  });
  const data = await parseJson(res);
  return {
    trackableIds: Array.isArray(data?.data?.trackableIds) ? data.data.trackableIds : [],
    maxAgeMinutes: Number(data?.data?.maxAgeMinutes) || 30,
  };
}

/** Batch leve: almoço aberto (startedAt ISO UTC). Sem lat/lng. */
export async function fetchTechnicianPanelActiveLunchBatch(technicianIds) {
  const ids = Array.isArray(technicianIds)
    ? [...new Set(technicianIds.map((id) => String(id ?? '').trim()).filter(Boolean))]
    : [];
  if (!ids.length) return { lunches: [] };
  const res = await fetch(`${API_BASE_URL}/api/technician-panel/lunch/active-batch`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ technicianIds: ids }),
  });
  const data = await parseJson(res);
  return {
    lunches: Array.isArray(data?.data?.lunches) ? data.data.lunches : [],
  };
}

export async function fetchTechnicianRouteOrders(technicianId, forecastYmd) {
  const qs = forecastYmd ? `?forecast=${encodeURIComponent(forecastYmd)}` : '';
  const res = await fetch(
    `${API_BASE_URL}/api/route/technician-orders/${encodeURIComponent(technicianId)}${qs}`,
    { headers: authHeaders() }
  );
  const data = await parseJson(res);
  if (Array.isArray(data.data?.orders)) return data.data.orders;
  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data.orders)) return data.orders;
  return [];
}

export async function geocodeBatch(addresses) {
  const res = await fetch(`${API_BASE_URL}/api/geocode/batch`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ addresses }),
  });
  const data = await parseJson(res);
  return Array.isArray(data.data) ? data.data : [];
}

export async function fetchGoogleMapsBrowserKey() {
  const res = await fetch(`${API_BASE_URL}/api/maps/google-browser-key`, {
    headers: authHeaders(),
  });
  const data = await parseJson(res);
  return data.key || null;
}

/** Snapshot do dia (query OS + DESLOC_TRAJETO) para o Painel do Técnico. dateYmd opcional (YYYY-MM-DD). */
export async function fetchTechnicianPanelDay(technicianId, dateYmd) {
  const qs =
    dateYmd && /^\d{4}-\d{2}-\d{2}$/.test(String(dateYmd).trim())
      ? `?date=${encodeURIComponent(String(dateYmd).trim())}`
      : '';
  const res = await fetch(
    `${API_BASE_URL}/api/technician-panel/${encodeURIComponent(technicianId)}/day${qs}`,
    { headers: authHeaders() }
  );
  const data = await parseJson(res);
  return data.data || null;
}

/** Foto de perfil do técnico (signed URL sob demanda — usuário tipo Técnico com mesmo codtec). */
export async function fetchTechnicianPanelProfilePhotoUrl(technicianId) {
  if (technicianId == null || String(technicianId).trim() === '') return null;
  const res = await fetch(
    `${API_BASE_URL}/api/technician-panel/${encodeURIComponent(String(technicianId).trim())}/profile-photo/url`,
    { headers: authHeaders() }
  );
  const data = await parseJson(res);
  return data?.success && data.viewUrl ? String(data.viewUrl) : null;
}

/** Resumo leve da OS (1 query) — modal do pin no rastreio. */
export async function fetchTechnicianPanelOsSummary(numeroOs) {
  const res = await fetch(
    `${API_BASE_URL}/api/technician-panel/os/${encodeURIComponent(numeroOs)}/summary`,
    { headers: authHeaders() }
  );
  const data = await parseJson(res);
  return data.data || null;
}

/** Detalhe de fechamento da OS (drawer): básicos, peças e fotos. */
export async function fetchTechnicianPanelOsDetail(numeroOs) {
  const res = await fetch(
    `${API_BASE_URL}/api/technician-panel/os/${encodeURIComponent(numeroOs)}/detail`,
    { headers: authHeaders() }
  );
  const data = await parseJson(res);
  return data.data || null;
}

export async function updateOrderSequence({ technicianId, osCode, newSequence }) {
  const res = await fetch(`${API_BASE_URL}/api/orders/update-sequence`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ technicianId, osCode, newSequence }),
  });
  return parseJson(res);
}

export async function addOrderToRoute({ technicianId, orderNumber, sequence, forecast }) {
  const res = await fetch(`${API_BASE_URL}/api/route/add-order`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ technicianId, orderNumber, sequence, forecast }),
  });
  return parseJson(res);
}

/** Persistência em lote (include + remove) + espelho MySQL. */
export async function saveRouteBatch({ technicianId, forecast, items, removedOs, source }) {
  const res = await fetch(`${API_BASE_URL}/api/route/save-batch`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({
      technicianId,
      forecast,
      items: items || [],
      removedOs: removedOs || [],
      source: source || 'technician_panel',
    }),
  });
  return parseJson(res);
}

export async function updateRouteSequence({ technicianId, orderNumber, newSequence, forecast }) {
  const res = await fetch(`${API_BASE_URL}/api/route/update-sequence`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify({ technicianId, orderNumber, newSequence, forecast }),
  });
  return parseJson(res);
}

export async function removeOrderFromRouteByOs({ orderNumber }) {
  const res = await fetch(`${API_BASE_URL}/api/route/remove-order-by-os`, {
    method: 'DELETE',
    headers: authHeaders(),
    body: JSON.stringify({ orderNumber }),
  });
  return parseJson(res);
}

/**
 * Gera link público do roteiro (snapshot + token). Requer sessão.
 */
export async function createTechnicianRoadmapShare({
  technicianId,
  day,
  osSequence,
  technicianName,
}) {
  const res = await fetch(
    `${API_BASE_URL}/api/technician-panel/${encodeURIComponent(technicianId)}/roadmap-share`,
    {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ day, osSequence, technicianName }),
    }
  );
  const data = await parseJson(res);
  return data.data || null;
}

export async function revokeTechnicianRoadmapShare({ technicianId, day, forecast }) {
  const res = await fetch(
    `${API_BASE_URL}/api/technician-panel/${encodeURIComponent(technicianId)}/roadmap-share/revoke`,
    {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ day, forecast }),
    }
  );
  return parseJson(res);
}

/** Limpa LCMY_REG_ROADMAP do técnico no forecast (roteiro vazio). */
export async function clearTechnicianMapPlanRoute({ technicianId, day, forecast }) {
  const res = await fetch(
    `${API_BASE_URL}/api/technician-panel/${encodeURIComponent(technicianId)}/map-plan/route`,
    {
      method: 'DELETE',
      headers: authHeaders(),
      body: JSON.stringify({ day, forecast }),
    }
  );
  return parseJson(res);
}

/**
 * Página pública (sem auth). Retorna `data` com status ok | not_yet_valid | expired | revoked | invalid.
 */
export async function fetchPublicTechnicianRoadmap(token) {
  const qs = `?t=${encodeURIComponent(String(token || '').trim())}`;
  const res = await fetch(`${API_BASE_URL}/api/technician-panel/public/roadmap${qs}`, {
    headers: { Accept: 'application/json' },
    credentials: 'omit',
    cache: 'no-store',
  });
  const body = await res.json().catch(() => ({}));
  if (body?.data) return body.data;
  if (!res.ok) {
    throw new Error(body.message || `Erro HTTP ${res.status}`);
  }
  return body.data || { status: 'invalid', message: 'Link inválido ou expirado.' };
}
